import { useState, useEffect, useRef } from 'react';
import { Accessibility, Type, EyeOff, Minus, Plus, X, RefreshCcw, Contrast } from 'lucide-react';

const STORAGE_KEY = 'emina_a11y';
const DEFAULTS = { font_scale: 100, high_contrast: false, dyslexic: false, reduce_motion: false };

function applyPrefs(prefs) {
  const root = document.documentElement;
  root.style.setProperty('--a11y-font-scale', `${prefs.font_scale}%`);
  root.classList.toggle('a11y-high-contrast', !!prefs.high_contrast);
  root.classList.toggle('a11y-dyslexic', !!prefs.dyslexic);
  root.classList.toggle('a11y-reduce-motion', !!prefs.reduce_motion);
}

export function useA11yInit() {
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const prefs = raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
      applyPrefs(prefs);
    } catch {
      applyPrefs(DEFAULTS);
    }
  }, []);
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
    } catch { return DEFAULTS; }
  });
  const panelRef = useRef(null);

  useEffect(() => {
    applyPrefs(prefs);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch {}
  }, [prefs]);

  useEffect(() => {
    if (!open) return;
    function onClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const set = (k, v) => setPrefs(p => ({ ...p, [k]: v }));
  const reset = () => setPrefs(DEFAULTS);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Pristupačnost"
        className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
        data-testid="a11y-toggle"
      >
        <Accessibility size={18} />
      </button>
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-50 p-4"
          data-testid="a11y-panel"
          role="dialog"
          aria-label="Opcije pristupačnosti"
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100">Pristupačnost</h4>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"><X size={16} /></button>
          </div>

          {/* Font size */}
          <div className="mb-4">
            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-2"><Type size={14} /> Veličina teksta</label>
            <div className="flex items-center gap-2">
              <button onClick={() => set('font_scale', Math.max(80, prefs.font_scale - 10))} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200" data-testid="a11y-font-dec"><Minus size={14} /></button>
              <span className="flex-1 text-center text-sm font-semibold text-gray-900 dark:text-gray-100" data-testid="a11y-font-value">{prefs.font_scale}%</span>
              <button onClick={() => set('font_scale', Math.min(140, prefs.font_scale + 10))} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200" data-testid="a11y-font-inc"><Plus size={14} /></button>
            </div>
          </div>

          {/* Toggles */}
          <ToggleRow
            icon={<Contrast size={14} />}
            label="Visok kontrast"
            checked={prefs.high_contrast}
            onChange={v => set('high_contrast', v)}
            testid="a11y-contrast"
          />
          <ToggleRow
            icon={<Type size={14} />}
            label="Font za disleksiju"
            checked={prefs.dyslexic}
            onChange={v => set('dyslexic', v)}
            testid="a11y-dyslexic"
          />
          <ToggleRow
            icon={<EyeOff size={14} />}
            label="Smanji animacije"
            checked={prefs.reduce_motion}
            onChange={v => set('reduce_motion', v)}
            testid="a11y-motion"
          />

          <button
            onClick={reset}
            className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 border border-gray-200 dark:border-gray-800 rounded-lg"
            data-testid="a11y-reset"
          >
            <RefreshCcw size={12} /> Vrati zadano
          </button>
        </div>
      )}
    </div>
  );
}

function ToggleRow({ icon, label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between py-2 cursor-pointer" data-testid={testid}>
      <span className="flex items-center gap-2 text-xs text-gray-800 dark:text-gray-200">{icon} {label}</span>
      <span className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-red-600' : 'bg-gray-300 dark:bg-gray-700'}`}>
        <input type="checkbox" className="sr-only" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </span>
    </label>
  );
}
